import { ChartConfigWithPopulatedData } from '@/features/charts/api/types';
import {
  AggregationType,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { supabase } from '@/lib/supabase';
import {
  DerivativeKpi,
  KpiTrackingEntry,
  KpiWithEntries,
  ProjectKpi,
  ProjectType,
} from '@/shared/types/api';
import { MONTH_IN_MS } from '@/shared/utils/date-utils';

interface KpiAccumulatedLatestValueResult {
  aggregatedValue: number;
  subprojectCount: number;
  lastEntryDate: string | null;
}

interface KpiTimelineAggregatedEntry {
  periodStart: string;
  aggregatedValue: number;
  subprojectCount: number;
}

// Constants
const CHART_DATA_FILTER_MONTHS = 3;
const DEFAULT_TIMELINE_PERIOD = 'monthly' as const;

// Type Guards
function isSubproject(projectType?: ProjectType): boolean {
  return (
    projectType === 'subproject_client' ||
    projectType === 'subproject_contractor'
  );
}

function isMainProject(projectType?: ProjectType): boolean {
  return projectType === 'project';
}

function isAccumulatedChartType(chartType: string): boolean {
  return [ChartType.BATTERY, ChartType.BAR, ChartType.PIE].includes(
    chartType as ChartType,
  );
}

function isTimelineChartType(chartType: string): boolean {
  return chartType === ChartType.LINE;
}

// Helper Functions
async function loadChartsFromDatabase(id: number) {
  const { data, error } = await supabase
    .from('chart_configs')
    .select(
      `
    *,
    chart_config_kpis (
      project_kpis (
        *,
        kpi_tracking_entries(*)
      )
    )
    `,
    )
    .eq('project_id', id)
    .gt(
      'chart_config_kpis.project_kpis.kpi_tracking_entries.created_at',
      new Date(
        Date.now() - CHART_DATA_FILTER_MONTHS * MONTH_IN_MS,
      ).toISOString(),
    )
    .order('updated_at', { ascending: false, nullsFirst: false });

  if (error) {
    throw error;
  }

  return data || [];
}

function transformChartData(
  data: Awaited<ReturnType<typeof loadChartsFromDatabase>>,
): ChartConfigWithPopulatedData[] {
  return data.map((chart) => ({
    ...chart,
    kpis: chart.chart_config_kpis
      .map((kpi) => {
        const projectKpi = kpi.project_kpis;
        if (!projectKpi) return null;

        return {
          ...projectKpi,
          entries: projectKpi.kpi_tracking_entries,
        };
      })
      .filter((kpi): kpi is NonNullable<typeof kpi> => kpi !== null),
  })) as ChartConfigWithPopulatedData[];
}

function trimToRelativeWindow(entries: KpiTrackingEntry[]): KpiTrackingEntry[] {
  if (entries.length === 0) return entries;

  // Find the latest entry date
  const latestDate = entries.reduce((max, entry) => {
    const d = entry.created_at ? new Date(entry.created_at).getTime() : 0;
    return d > max ? d : max;
  }, 0);

  if (latestDate === 0) return entries;

  // Keep entries within CHART_DATA_FILTER_MONTHS before the latest entry
  const cutoff = latestDate - CHART_DATA_FILTER_MONTHS * MONTH_IN_MS;
  return entries.filter((entry) => {
    if (!entry.created_at) return false;
    return new Date(entry.created_at).getTime() >= cutoff;
  });
}

async function enrichKpisWithSubprojectEntries(
  charts: ChartConfigWithPopulatedData[],
  projectId: number,
): Promise<ChartConfigWithPopulatedData[]> {
  return Promise.all(
    charts.map(async (chart) => {
      const kpisWithEntries = await Promise.all(
        chart.kpis.map(async (kpi) => {
          try {
            // Fetch without date filter (SQL defaults to 12 months)
            const kpiWithData = await getChartDataForKpi(
              kpi.id,
              projectId,
              undefined,
              false,
              undefined,
              undefined,
            );

            // Trim to last 3 months relative to the latest entry
            const entries = kpiWithData.entries ?? [];
            const trimmedEntries = trimToRelativeWindow(entries);

            return {
              ...kpi,
              entries: trimmedEntries,
              max_value: kpiWithData.max_value,
            };
          } catch {
            // Fallback to original entries if getChartDataForKpi fails
            return kpi;
          }
        }),
      );
      return { ...chart, kpis: kpisWithEntries };
    }),
  );
}

function createSyntheticEntry(
  kpiId: number,
  projectId: number,
  value: number,
  date: string,
): KpiTrackingEntry {
  return {
    id: 0,
    kpi_id: kpiId,
    project_id: projectId,
    report_id: 0,
    value,
    created_at: date,
    updated_at: null,
    created_by: null,
    updated_by: null,
  };
}

async function applyAccumulatedAggregation(
  chart: ChartConfigWithPopulatedData,
  projectId: number,
): Promise<ChartConfigWithPopulatedData> {
  const kpisWithSyntheticEntries = await Promise.all(
    chart.kpis.map(async (kpi) => {
      const aggregated = await getKpiAccumulatedLatestValue({
        projectId,
        kpiId: kpi.id,
      });

      // Fallback to existing entries when aggregation has no data
      if (!aggregated || aggregated.subprojectCount <= 1) return kpi;

      const syntheticEntry = createSyntheticEntry(
        kpi.id,
        projectId,
        aggregated.aggregatedValue,
        aggregated.lastEntryDate ?? new Date().toISOString(),
      );

      return {
        ...kpi,
        entries: [syntheticEntry],
      };
    }),
  );

  return {
    ...chart,
    kpis: kpisWithSyntheticEntries,
  };
}

async function applyTimelineAggregation(
  chart: ChartConfigWithPopulatedData,
  projectId: number,
): Promise<ChartConfigWithPopulatedData> {
  const kpisWithTimelineEntries = await Promise.all(
    chart.kpis.map(async (kpi) => {
      const aggregatedEntries = await getKpiTimelineAggregatedValues({
        projectId,
        kpiId: kpi.id,
        period: DEFAULT_TIMELINE_PERIOD,
      });

      // Fallback to existing entries when aggregation has no data
      if (aggregatedEntries.length === 0) return kpi;

      // Check if we have data from multiple subprojects
      const hasMultipleSubprojects = aggregatedEntries.some(
        (entry) => entry.subprojectCount > 1,
      );
      if (!hasMultipleSubprojects) return kpi;

      // Convert aggregated entries to KpiTrackingEntry format
      const syntheticEntries: KpiTrackingEntry[] = aggregatedEntries.map(
        (entry) =>
          createSyntheticEntry(
            kpi.id,
            projectId,
            entry.aggregatedValue,
            entry.periodStart,
          ),
      );

      return {
        ...kpi,
        entries: syntheticEntries,
      };
    }),
  );

  return {
    ...chart,
    kpis: kpisWithTimelineEntries,
  };
}

async function applyAggregationsForMainProject(
  charts: ChartConfigWithPopulatedData[],
  projectId: number,
): Promise<ChartConfigWithPopulatedData[]> {
  return Promise.all(
    charts.map(async (chart) => {
      const isAccumulated =
        chart.aggregation_type === AggregationType.ACCUMULATED;
      const isTimeline = chart.aggregation_type === AggregationType.TIMELINE;

      // Handle accumulated charts (Battery, Bar, Pie)
      if (isAccumulated && isAccumulatedChartType(chart.chart_type)) {
        return applyAccumulatedAggregation(chart, projectId);
      }

      // Handle timeline charts (Line and Bar)
      if (
        isTimeline &&
        (isTimelineChartType(chart.chart_type) ||
          chart.chart_type === ChartType.BAR)
      ) {
        return applyTimelineAggregation(chart, projectId);
      }

      return chart;
    }),
  );
}

export const getChartsForProject = async (
  id: number,
  projectType?: ProjectType,
): Promise<ChartConfigWithPopulatedData[]> => {
  const chartsData = await loadChartsFromDatabase(id);
  const transformedCharts = transformChartData(chartsData);

  if (isSubproject(projectType)) {
    return enrichKpisWithSubprojectEntries(transformedCharts, id);
  }

  if (!isMainProject(projectType)) {
    return transformedCharts;
  }

  return applyAggregationsForMainProject(transformedCharts, id);
};

export async function getKpiAccumulatedLatestValue({
  projectId,
  kpiId,
}: {
  projectId: number;
  kpiId: number;
}): Promise<KpiAccumulatedLatestValueResult | null> {
  const { data, error } = await supabase.rpc(
    'get_kpi_accumulated_latest_value',
    {
      p_project_id: projectId,
      p_kpi_id: kpiId,
    },
  );

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;

  return {
    aggregatedValue: Number(row.aggregated_value ?? 0),
    subprojectCount: Number(row.subproject_count ?? 0),
    lastEntryDate: (row.last_entry_date as string | null) ?? null,
  };
}

export async function getKpiTimelineAggregatedValues({
  projectId,
  kpiId,
  period = 'monthly',
  fromDate,
  toDate,
}: {
  projectId: number;
  kpiId: number;
  period?: 'daily' | 'weekly' | 'monthly';
  fromDate?: Date;
  toDate?: Date;
}): Promise<KpiTimelineAggregatedEntry[]> {
  const { data, error } = await supabase.rpc(
    'get_kpi_timeline_aggregated_values',
    {
      p_project_id: projectId,
      p_kpi_id: kpiId,
      p_period: period,
      p_from_date: fromDate?.toISOString(),
      p_to_date: toDate?.toISOString(),
    },
  );

  if (error) throw error;

  if (!data || !Array.isArray(data)) return [];

  return data.map((row) => ({
    periodStart: row.period_start as string,
    aggregatedValue: Number(row.aggregated_value ?? 0),
    subprojectCount: Number(row.subproject_count ?? 0),
  }));
}

export const getChartDataForKpi = async (
  id: number,
  projectId: number,
  monthsBack?: number,
  isDerivative?: boolean,
  fromDate?: Date,
  toDate?: Date,
): Promise<KpiWithEntries> => {
  if (isDerivative) {
    const { data: kpiData, error: kpiError } = await supabase
      .from('derivative_kpis')
      .select('*')
      .eq('id', id)
      .single();

    if (kpiError) {
      throw kpiError;
    }

    const { data: metricsData, error: metricsError } = await supabase
      .from('derivative_kpi_metrics_view')
      .select('*')
      .eq('id', id)
      .order('last_entry_date', { ascending: true });

    if (metricsError) {
      throw metricsError;
    }

    let filteredMetrics = metricsData || [];

    if (fromDate || toDate) {
      filteredMetrics = filteredMetrics.filter((metric) => {
        if (!metric.last_entry_date) return false;
        const entryDate = new Date(metric.last_entry_date);
        if (fromDate && entryDate < fromDate) return false;
        if (toDate && entryDate > toDate) return false;
        return true;
      });
    } else if (monthsBack) {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);

      filteredMetrics = filteredMetrics.filter((metric) => {
        if (!metric.last_entry_date) return false;
        return new Date(metric.last_entry_date) >= cutoffDate;
      });
    }

    const entries = filteredMetrics.map((metric) => ({
      id: 0,
      kpi_id: id,
      project_id: metric.project_id || 0,
      report_id: 0,
      value: metric.latest_entry_value ?? metric.total_value ?? null,
      created_at: metric.last_entry_date || new Date().toISOString(),
      updated_at: null,
      created_by: null,
      updated_by: null,
    })) as KpiTrackingEntry[];

    return {
      ...kpiData,
      entries,
    };
  }

  const { data, error } = await supabase.rpc('get_kpi_with_limited_entries', {
    p_kpi_id: id,
    p_project_id: projectId,
    p_months_back: fromDate || toDate ? undefined : monthsBack,
    p_from_date: fromDate?.toISOString(),
    p_to_date: toDate?.toISOString(),
  });

  if (error) {
    throw error;
  }

  const parsedData = data as {
    kpi: ProjectKpi | DerivativeKpi;
    budget: number | null | undefined;
    entries: KpiTrackingEntry[];
  };

  return {
    ...parsedData.kpi,
    max_value: parsedData.budget ?? parsedData.kpi.max_value,
    entries: parsedData.entries,
  };
};
